import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';

interface OrderItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
}

interface Order {
  id: string;
  orderNumber: string;
  customerName: string;
  email: string;
  phone: string;
  address: string;
  items: OrderItem[];
  total: number;
  subtotal: number;
  shipping: number;
  tax: number;
  paymentMethod: string;
  status: string;
  date: string;
}

export default function MyOrdersPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadOrders = () => {
      try {
        const savedOrders = JSON.parse(localStorage.getItem('customerOrders') || '[]');
        setOrders(savedOrders);
      } catch (error) {
        console.error('Failed to load orders:', error);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'shipped':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mb-6">
            <i className="ri-shopping-bag-line text-gray-400 text-2xl"></i>
          </div>
          <h1 className="text-3xl font-semibold text-gray-900 mb-4">No Orders Yet</h1>
          <p className="text-gray-600 mb-8">You haven't placed any orders yet. Start shopping to see your orders here.</p>
          <Link 
            to="/products" 
            className="bg-green-600 text-white px-8 py-3 rounded-full hover:bg-green-700 transition-colors whitespace-nowrap"
          >
            Shop Products
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="py-6 sm:py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">My Orders</h1>
            <p className="text-sm sm:text-base text-gray-600">Track and manage your orders</p>
          </div>

          <div className="space-y-4">
            {orders.map((order) => (
              <div key={order.id} className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
                <div className="flex flex-col space-y-3 sm:space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                    <div className="flex flex-col space-y-1">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900">{order.orderNumber}</h3>
                      <p className="text-xs sm:text-sm text-gray-600">Placed on {formatDate(order.date)}</p>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end space-x-3">
                      <span className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-full ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                      <div className="text-right">
                        <p className="text-xs sm:text-sm text-gray-600">Total</p>
                        <p className="text-base sm:text-lg font-semibold text-gray-900">₹{order.total.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => setSelectedOrder(order)}
                    className="w-full sm:w-auto sm:self-end bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base"
                  >
                    View Details
                  </button>
                </div>

                <div className="border-t pt-3 sm:pt-4">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 sm:gap-4">
                    {order.items.slice(0, 3).map((item) => (
                      <div key={item.id} className="flex items-center space-x-3">
                        <img 
                          src={item.image} 
                          alt={item.name} 
                          className="w-10 h-10 sm:w-12 sm:h-12 object-cover rounded-lg flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">{item.name}</p>
                          <p className="text-xs text-gray-600">Qty: {item.quantity}</p>
                        </div>
                      </div>
                    ))}
                    {order.items.length > 3 && (
                      <div className="flex items-center justify-center text-xs sm:text-sm text-gray-600 col-span-full sm:col-span-1">
                        +{order.items.length - 3} more items
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3 sm:p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b">
              <div className="flex items-start justify-between">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800 pr-4 leading-tight">
                  Order Details - {selectedOrder.orderNumber}
                </h2>
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 flex-shrink-0"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Order Status */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                <div>
                  <span className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-full ${getStatusColor(selectedOrder.status)}`}>
                    {selectedOrder.status}
                  </span>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">
                    Order placed on {formatDate(selectedOrder.date)}
                  </p>
                </div>
                <div className="text-left sm:text-right">
                  <p className="text-xs sm:text-sm text-gray-600">Total Amount</p>
                  <p className="text-xl sm:text-2xl font-bold text-green-600">₹{selectedOrder.total.toFixed(2)}</p>
                </div>
              </div>

              {/* Customer Info */}
              <div>
                <h3 className="text-base sm:text-lg font-medium text-gray-800 mb-3">Shipping Address</h3>
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                  <p className="font-medium text-gray-900 text-sm sm:text-base">{selectedOrder.customerName}</p>
                  <p className="text-gray-700 mt-1 text-sm sm:text-base leading-relaxed">{selectedOrder.address}</p>
                  <div className="flex flex-col space-y-1 sm:space-y-0 sm:flex-row sm:space-x-4 mt-2">
                    <p className="text-gray-700 text-sm sm:text-base break-all">Email: {selectedOrder.email}</p>
                    <p className="text-gray-700 text-sm sm:text-base">Phone: {selectedOrder.phone}</p>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div>
                <h3 className="text-base sm:text-lg font-medium text-gray-800 mb-3">Order Items</h3>
                <div className="space-y-3">
                  {selectedOrder.items.map((item) => (
                    <div key={item.id} className="flex items-center space-x-3 sm:space-x-4 p-3 bg-gray-50 rounded-lg">
                      <img 
                        src={item.image} 
                        alt={item.name} 
                        className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-lg flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm sm:text-base truncate">{item.name}</p>
                        <p className="text-xs sm:text-sm text-gray-600">Quantity: {item.quantity}</p>
                        <p className="text-xs sm:text-sm text-gray-600">Price: ₹{item.price.toFixed(2)} each</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-medium text-gray-900 text-sm sm:text-base">₹{(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Summary */}
              <div>
                <h3 className="text-base sm:text-lg font-medium text-gray-800 mb-3">Order Summary</h3>
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 space-y-2">
                  <div className="flex justify-between text-sm sm:text-base">
                    <span className="text-gray-700">Subtotal</span>
                    <span className="text-gray-900">₹{selectedOrder.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm sm:text-base">
                    <span className="text-gray-700">Shipping</span>
                    <span className="text-gray-900">
                      {selectedOrder.shipping === 0 ? 'FREE' : `₹${selectedOrder.shipping.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm sm:text-base">
                    <span className="text-gray-700">Tax</span>
                    <span className="text-gray-900">₹{selectedOrder.tax.toFixed(2)}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-semibold text-base sm:text-lg">
                    <span className="text-gray-900">Total</span>
                    <span className="text-green-600">₹{selectedOrder.total.toFixed(2)}</span>
                  </div>
                  <div className="mt-2 pt-2 border-t">
                    <p className="text-xs sm:text-sm text-gray-600">Payment Method: {selectedOrder.paymentMethod}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
